import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { authActions, authCRUD } from "../../../actions/auth";
import { Input, Checkbox, Button, Alert, Divider } from "antd";
import {
  UserOutlined,
  LockOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  LoginOutlined,
} from "@ant-design/icons";
import { useAuth } from "../../../context/AuthContext";

const loginSchema = Yup.object().shape({
  username: Yup.string().required("Username is required"),
  password: Yup.string().required("Password is required"),
});

const initialValues = {
  username: "",
  password: "",
};

export const LoginContent = () => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { getOtp } = useAuth();
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: (values, { setStatus, setSubmitting, resetForm }) => {
      setLoading(true);
      setStatus(null);
      const { username, password } = values;

      authCRUD
        .suzaLogin(username, password)
        .then((resp) => {
          const { accessToken, user } = resp.data;

          const localUser = {
            id: user.userID,
            username: user.userName,
            email: user.email,
            first_name: user.firstName,
            last_name: user.lastName,
            phone: user.phoneNumber,
            is_active: user.platform?.status === 1,
            role: user.platform?.roles?.role || "User",
            roleCode: user.platform?.roles?.roleCode,
            platform: user.platform?.platformEntity?.platformName,
          };

          dispatch(authActions.getUser(localUser));
          dispatch(authActions.getToken(accessToken));
          dispatch(authActions.setAuth(true));

          if (user.platform?.platformEntity?.allowOTP === 1) {
            getOtp({
              email: user.email,
              name: user.firstName,
              time: 3,
            });
            navigate("/otp");
          } else {
            navigate("/home");
          }

          setLoading(false);
        })
        .catch((error) => {
          setLoading(false);
          setSubmitting(false);
          const errorMsg =
            error.response?.data?.message || "The login details are incorrect";
          setStatus(errorMsg);
        });
    },
  });

  return (
    <div className="login-page">
      {/* Left Branding Panel */}
      <div className="login-branding">
        <div className="branding-overlay">
          <img
            src="assets/images/suza.jpeg"
            alt="SUZA Logo"
            className="branding-logo"
          />
          <h1 className="branding-title">
            The State University of Zanzibar
          </h1>
          <h2 className="branding-subtitle">
            Master Timetable Management System
          </h2>
          <div className="branding-divider"></div>
          <p className="branding-tagline">
            Catalyst for Social Change
          </p>
          <div className="branding-features">
            <div className="feature-item">
              <i className="ri-calendar-schedule-line"></i>
              <span>Smart Scheduling</span>
            </div>
            <div className="feature-item">
              <i className="ri-building-line"></i>
              <span>Venue Management</span>
            </div>
            <div className="feature-item">
              <i className="ri-team-line"></i>
              <span>Course Allocations</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Form Panel */}
      <div className="login-form-panel">
        <div className="login-form-container">
          {/* Mobile Logo (hidden on desktop) */}
          <div className="mobile-logo-container">
            <img
              src="assets/images/suza.jpeg"
              alt="SUZA"
              className="mobile-logo"
            />
          </div>

          <h3 className="login-welcome">Welcome Back</h3>
          <p className="login-subtitle">
            Sign in to manage your timetable
          </p>

          {formik.status && (
            <Alert
              message={formik.status}
              type="error"
              showIcon
              closable
              style={{ marginBottom: 20, borderRadius: 8 }}
              onClose={() => formik.setStatus(null)}
            />
          )}

          <form onSubmit={formik.handleSubmit}>
            <div className="form-field">
              <label className="form-label">Username</label>
              <Input
                prefix={<UserOutlined style={{ color: "#aaa" }} />}
                size="large"
                placeholder="Enter your username or email"
                {...formik.getFieldProps("username")}
                status={
                  formik.touched.username && formik.errors.username
                    ? "error"
                    : undefined
                }
                style={{ borderRadius: 8, height: 48 }}
              />
              {formik.touched.username && formik.errors.username && (
                <span className="field-error">{formik.errors.username}</span>
              )}
            </div>

            <div className="form-field">
              <label className="form-label">Password</label>
              <Input.Password
                prefix={<LockOutlined style={{ color: "#aaa" }} />}
                iconRender={(visible) =>
                  visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                }
                size="large"
                placeholder="Enter your password"
                {...formik.getFieldProps("password")}
                status={
                  formik.touched.password && formik.errors.password
                    ? "error"
                    : undefined
                }
                style={{ borderRadius: 8, height: 48 }}
              />
              {formik.touched.password && formik.errors.password && (
                <span className="field-error">{formik.errors.password}</span>
              )}
            </div>

            <div className="login-options">
              <Checkbox>Remember me</Checkbox>
            </div>

            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={loading}
              icon={!loading ? <LoginOutlined /> : null}
              className="login-button"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          <Divider style={{ margin: "24px 0 16px" }} />

          <div className="login-footer">
            <p className="footer-copyright">
              &copy; {new Date().getFullYear()} SUZA | Timetable System
            </p>
            <p className="footer-tagline">
              ---- catalyst for social change ----
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
